/**
 * /dashboard/posh/reports/$id — Annual report detail.
 *
 * GET /posh/reports/{id}. NOT 2FA-gated. COUNTS-ONLY stats (POSH Act
 * s.21). Read-only for everyone; notes edit + status transition controls
 * gated on usePoshAdmin AND the report state machine.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/posh/reports/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Download, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { poshApi, toApiError } from "@/lib/api";
import {
  useReport,
  useUpdateReport,
  useUpdateReportStatus,
} from "@/hooks/use-posh";
import { fmtDate } from "@/lib/posh-format";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { usePoshAdmin } from "@/components/posh/use-posh-admin";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import type {
  AnnualReport,
  AnnualReportData,
  AnnualReportStats,
} from "@/types/posh";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/reports/$id",
)({
  component: ReportDetailRoute,
});

// Mirrors models.reportTransitions (draft→generated→filed; terminal
// filed). Only the single legal next move is offered.
const REPORT_NEXT: Record<string, { value: string; label: string } | null> =
  {
    draft: { value: "generated", label: "Mark generated" },
    generated: { value: "filed", label: "Mark filed" },
    filed: null,
  };

const STAT_ROWS: { key: keyof AnnualReportStats; label: string }[] =
  [
    { key: "complaints_received", label: "Complaints received" },
    { key: "complaints_disposed", label: "Complaints disposed" },
    { key: "complaints_pending", label: "Complaints pending at year end" },
    { key: "cases_resolved", label: "Cases resolved / closed on inquiry" },
    { key: "cases_withdrawn", label: "Complaints withdrawn" },
    { key: "cases_rejected", label: "Complaints rejected / not admitted" },
    { key: "training_completed", label: "Training completions" },
    { key: "total_employees", label: "Active employees" },
    { key: "icc_members_active", label: "Active ICC members" },
  ];

function reportData(r: AnnualReport): AnnualReportData | null {
  const d: AnnualReportData | undefined = r.report_data;
  return d && typeof d === "object" ? d : null;
}

function ReportDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const q = useReport(numId);
  const isAdmin = usePoshAdmin();
  const notesMut = useUpdateReport(numId);
  const statusMut = useUpdateReportStatus(numId);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const notes = notesDraft ?? q.data?.report.notes ?? "";

  async function onDownload() {
    setPdfBusy(true);
    try {
      const blob = await poshApi.downloadReportPdf(numId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `workright-posh-annual-report-${q.data?.report.year ?? numId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setPdfBusy(false);
    }
  }

  async function onSaveNotes() {
    try {
      await notesMut.mutateAsync({ notes });
      toast.success("Notes saved.");
      setNotesDraft(null);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function onAdvance(next: string) {
    try {
      await statusMut.mutateAsync({ status: next });
      toast.success("Report status updated.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <PoshModuleShell
      title="Annual Report"
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/posh/reports" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <PoshFeatureGate>
        {q.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this report</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data &&
        (() => {
          const r = q.data.report;
          const data = reportData(r);
          const stats = data?.stats ?? null;
          const next = REPORT_NEXT[r.status];
          const isFiled = r.status === "filed";
          return (
            <div className="space-y-5">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        Annual report {r.year}
                      </CardTitle>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {data?.reporting_period ??
                          `01 Jan ${r.year} – 31 Dec ${r.year}`}
                        {r.filing_deadline
                          ? ` · Filing deadline ${fmtDate(r.filing_deadline)}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={r.status} />
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        disabled={pdfBusy}
                        onClick={onDownload}
                      >
                        {pdfBusy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        PDF
                      </Button>
                      {isAdmin && next && (
                        <Button
                          size="sm"
                          className="gap-1.5"
                          disabled={statusMut.isPending}
                          onClick={() => onAdvance(next.value)}
                        >
                          {statusMut.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                          {next.label}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {stats ? (
                    <dl className="grid gap-3 sm:grid-cols-2">
                      {STAT_ROWS.map((row) => (
                        <div
                          key={row.key}
                          className="flex items-center justify-between rounded-md border border-input px-3 py-2"
                        >
                          <dt className="text-sm text-muted-foreground">
                            {row.label}
                          </dt>
                          <dd className="text-sm font-semibold text-gray-900">
                            {stats[row.key]}
                          </dd>
                        </div>
                      ))}
                      <div className="flex items-center justify-between rounded-md border border-input px-3 py-2 sm:col-span-2">
                        <dt className="text-sm text-muted-foreground">
                          Training coverage
                        </dt>
                        <dd className="text-sm font-semibold text-gray-900">
                          {data?.training_coverage_pct ?? 0}%
                        </dd>
                      </div>
                    </dl>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Report aggregate is not available.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isAdmin && !isFiled ? (
                    <>
                      <Label className="sr-only">Notes</Label>
                      <Textarea
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder="Internal notes for this report (not part of the counts)."
                      />
                      <Button
                        size="sm"
                        disabled={notesMut.isPending}
                        onClick={onSaveNotes}
                        className="gap-1.5"
                      >
                        {notesMut.isPending && (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        )}
                        Save notes
                      </Button>
                    </>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {r.notes ||
                        (isFiled
                          ? "This report is filed and immutable."
                          : "No notes.")}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })()}
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
